<script setup>

import Shared from '@/common/shared'
import {Dictionary, Funds} from '@/common/api.service'
import {toRefs, defineProps, ref, onMounted} from 'vue'
import ValidateInput from '@/components/validate/input.vue'
import moment from 'moment'
import {useToast} from 'vue-toastification'

const toast = useToast()
const props = defineProps({
  fund: {},
  item: {},
})
const {fund, item,} = toRefs(props)
const itemFields = ref([])
const legalForms = ref([])
const setRef = (el) => {
  itemFields.value.push(el)
}
const isSaveForm = ref(true)

async function save() {
  let isValid = true
  itemFields.value.forEach((f) => {
    f.validate()
    if (f.validationFailed) {
      isValid = false
    }
  })

  if (fund.value.siteUrl === '') {
    delete fund.value.siteUrl
  }

  if (fund.value.generalEmail === '') {
    delete fund.value.generalEmail
  }

  if (fund.value.agreementSigningDate) {
    fund.value.agreementSigningDate = moment(fund.value.agreementSigningDate).format('YYYY-MM-DD')
  }

  fund.value.legalForm = parseInt(fund.value.legalFormTypes)

  if (isValid) {
    await post().then(async () => {
      if (isSaveForm.value) {
        close()
      }
    }).catch(async ({response}) => {
      if (response.data !== undefined && response.data.title) {
        toast.error(response.data.title, {
          timeout: 5000
        })
      }
      if (response.data !== undefined && response.data.errors) {
        toast.error(response.data.title, {
          timeout: 5000
        })
      }
    })
  } else {
    toast.error('Основная информация о РФРП не сохранена, проверьте обязательные поля', {
      timeout: 5000
    })
  }
}

async function close() {
  Shared.closeModal('#edit-main-rfrp')
  fund.value.modal = false
}

async function post() {

  let setData = {
    "olkId": fund.value.id,
    "siteUrl": fund.value.siteUrl,
    "generalEmail": fund.value.generalEmail,
    "generalPhone": fund.value.generalPhone,
    "legalForm": parseInt(fund.value.legalFormTypes),
    "legalAddress": fund.value.legalAddress,
    "actualAddress": fund.value.actualAddress,
    "frpFounder": fund.value.frpFounder,
    "npaRequisites": fund.value.npaRequisites,
    "agreementSigningDate": fund.value.agreementSigningDate,
    "ogrn": fund.value.ogrn,
    "legalTitle": fund.value.legalTitle,
    "shortTitle": fund.value.shortTitle,
  }

  await Funds.updateFunds(fund.value.id, setData)
      .then(async () => {
        isSaveForm.value = true
        toast.success("Данные формы изменены", {
          timeout: 5000
        })
      })
      .catch(async (response) => {
        isSaveForm.value = false
        if (response.response.data.errors['$.legalForm']) {
          response.response.data.errors['$.legalForm'].forEach((f) => {
            toast.error(f, {
              timeout: 5000
            })
          })
        }
        if (response.response.data.errors['Body.LegalTitle']){
          response.response.data.errors['Body.LegalTitle'].forEach((f) => {
            toast.error(f, {
              timeout: 5000
            })
          })
        }
        if (response.response.data.errors['Body.GeneralEmail']){
          response.response.data.errors['Body.GeneralEmail'].forEach((f) => {
            toast.error(f, {
              timeout: 5000
            })
          })
        }
      })
  await Funds.item(fund.value.id)
      .then(({data}) => {
        item.value.fund = data
      }).catch(() => {
      })
}

onMounted(async () => {
  await Dictionary.legalForms()
      .then(({data}) => {
        legalForms.value = data
      }).catch(() => {
      })
  if (!fund.value.generalPhone) {
    fund.value.generalPhone = '+7'
  }
  Shared.showModal('#edit-main-rfrp')
})

</script>

<template>
  <div class="modal-overlay" id="edit-main-rfrp">
    <div class="popup popup-oy-auto">
      <div class="modal-title title-h3">Редактирование основной информации о РФРП</div>
      <div class="modal-body">
        <ValidateInput
            :name="'Сайт'"
            url
            placeholder="Введите сайт"
            maxlength="255"
            :ref="setRef"
            v-model="fund.siteUrl">
        </ValidateInput>
        <ValidateInput
            :name="'Общий e-mail'"
            placeholder="Введите адрес электронной почты"
            :ref="setRef"
            maxlength="255"
            v-model="fund.generalEmail">
        </ValidateInput>
        <ValidateInput
            :name="'Номер телефона'"
            placeholder="Введите номер телефона"
            :ref="setRef"
            maxlength="255"
            v-model="fund.generalPhone">
        </ValidateInput>
        <ValidateInput
            :name="'Организационно-правовая форма'"
            required
            :ref="setRef"
            select
            :items="legalForms"
            :item-key="'value'"
            :item-name="'title'"
            v-model="fund.legalFormTypes">
        </ValidateInput>
        <ValidateInput
            :name="'Фактический адрес'"
            placeholder="Введите адрес"
            maxlength="1024"
            :ref="setRef"
            v-model="fund.actualAddress">
        </ValidateInput>
        <ValidateInput
            :name="'Юридически адрес'"
            placeholder="Введите адрес"
            maxlength="1024"
            :ref="setRef"
            v-model="fund.legalAddress">
        </ValidateInput>
        <ValidateInput
            :name="'Учредитель РФРП'"
            placeholder="Введите учредителя РФРП"
            maxlength="255"
            :ref="setRef"
            v-model="fund.frpFounder">
        </ValidateInput>
        <ValidateInput
            :name="'Реквизиты НПА о создании РФРП'"
            placeholder="Введите реквизиты НПА о создании РФРП"
            :ref="setRef"
            maxlength="1024"
            v-model="fund.npaRequisites">
        </ValidateInput>
        <label for="rfrp-date" class="title-field">Дата подписания соглашения
          <VueDatePicker placeholder="00.00.0000" class="field field--calendar"
                         v-model="fund.agreementSigningDate"/>
        </label>
      </div>
      <div class="modal-bottom">
        <div class="buttons-block">
          <button type="reset" class="button button--clear" @click="close">отменить
          </button>
          <button type="submit" class="button" @click="save()">СОХРАНИТЬ</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>